import Effects from 'effects/Effects.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import Tone from 'tone';
import Saturator from './Saturator.js';
import EQ8 from './EQ8.js';
import {AudioParamValueParameter,
        AbstractAudioParamParameter,
        } from 'parameters/AbstractAudioParamParameter.js';

class TapeDelay extends Effects.Effect {
    constructor(sequencerEngine) {
        super("Tape Delay", () => new Tone.Gain(1), sequencerEngine);

        this.initComponents();

        this.rateParameter = new AudioParamValueParameter(
            'Rate', this.delayNode.delayTime, {
                documentation: "The amount of time (in seconds) between echos.",
                defaultValue: 0.6,
                min: 0,
                max: 1},
            0.07,
            );

        this.registerParameter(this.rateParameter);

        this.feedbackParameter = new AudioParamValueParameter(
            'Feedback', this.feedbackNode.gain, {
                documentation: [
                    "The amount of delayed signal that is fed back into the effect",
                    "Be careful, past 50%-- depending on the saturation-- it can get unruly."
                ],
                defaultValue: 0.1,
                min: 0,
                max: 1
            }
            );

        this.registerParameter(this.feedbackParameter);

        this.wetParameter = new AudioParamValueParameter(
            'Wet', this.crossFade.fade, {
                documentation: "Controls the amount of (wet) effected signal, vs. dry (un-effected) signal.",
                defaultValue: 0.5,
                min: 0,
                max: 1});

        this.registerParameter(this.wetParameter);
        this.updateInitialValues();
    }

    get documentation() {
        return "Emulates a tape echo effect, where the signal is fed back into the effect, and saturated.";
    }

    get driveParameter() {
        return this.saturatorNode && this.saturatorNode.driveParameter;
    }

    get lowEQParameter() {
        return this.eqNode && this.eqNode.equalizerPoints[0].gainParameter;
    }

    get midEQParameter() {
        return this.eqNode && this.eqNode.equalizerPoints[4].gainParameter;
    }

    get highEQParameter() {
        return this.eqNode && this.eqNode.equalizerPoints[7].gainParameter;
    }

    initComponents() {
        this.inputNode = this.effectNode;
        this.crossFade = new Tone.CrossFade(0.5);
        this.feedbackNode = new Tone.Gain(0.35);
        this.saturatorNode = new Saturator(this.sequencerEngine);
        this.saturatorNode.driveParameter.set(.95);
        this.registerParameter(this.driveParameter);
        this.delayNode = new Tone.FeedbackDelay(0.1, 0.1);
        this.eqNode = new EQ8(this.sequencerEngine, false);
        this.eqNode.equalizerPoints[0].frequencyParameter.set(300);
        this.eqNode.equalizerPoints[4].qParameter.set(0.7);
        this.eqNode.equalizerPoints[7].frequencyParameter.set(4000);
        this.registerParameter(this.lowEQParameter);
        this.registerParameter(this.midEQParameter);
        this.registerParameter(this.highEQParameter);
        this.delayNode.wet.value = 1;

        // connect the components
        this.inputNode.connect(this.saturatorNode.effectNode);
        this.saturatorNode.connect(this.eqNode.effectNode);
        this.eqNode.connect(this.delayNode);

        // connect to crossfaders
        this.inputNode.connect(this.crossFade, 0, 0);
        this.delayNode.connect(this.crossFade, 0, 1);

        // connect the feedback node
        this.delayNode.connect(this.feedbackNode);
        this.feedbackNode.connect(this.saturatorNode.effectNode);

        this.outputNode = new Tone.Gain(1);

        this.crossFade.connect(this.outputNode);

   }

    connect(x) {
        this.outputNode.connect(x);
    }

    disconnect(x) {
        this.outputNode.disconnect(x);
    }

    dispose() {
        super.dispose();
        this.delayNode.dispose();
        this.eqNode.dispose();
        this.saturatorNode.dispose();
        this.feedbackNode.dispose();
        this.crossFade.dispose();
        this.outputNode.dispose();

        this.rateParameter = undefined;
        this.wetParameter = undefined;
        this.track = undefined;
        this.inputNode = undefined;
        this.feedbackParameter = undefined;
        this.delayNode = undefined;
        this.eqNode = undefined;
        this.saturatorNode = undefined;
        this.feedbackNode = undefined;
        this.crossFadeNode = undefined;
        this.outputNode = undefined;
        this.parameters = undefined;
        this.rateParameter = undefined;
        this.feedbackParameter = undefined;
        this.wetParameter = undefined;
        this.sequencerEngine = undefined;
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new TapeDelay(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }

    getJson() {
        let json = {
            ...super.getJson(),
            lowEQ: this.lowEQParameter.get(true),
            midEQ: this.midEQParameter.get(true),
            drive: this.driveParameter.get(true),
            highEQ: this.highEQParameter.get(true),
            feedback: this.feedbackParameter.get(true),
            wet: this.wetParameter.get(true),
            rate: this.rateParameter.get(true),
        };

        json = this.saveParameterToJson(json, 'driveParameter', this.driveParameter);
        json = this.saveParameterToJson(json, 'lowEQParameter', this.lowEQParameter);
        json = this.saveParameterToJson(json, 'midEQParameter', this.midEQParameter);
        json = this.saveParameterToJson(json, 'highEQParameter', this.highEQParameter);
        json = this.saveParameterToJson(json, 'feedbackParameter', this.feedbackParameter);
        json = this.saveParameterToJson(json, 'rateParameter', this.rateParameter);
        json = this.saveParameterToJson(json, 'wetParameter', this.wetParameter);
        return json;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        bulkUpdate(this.lowEQParameter, json.lowEQ, bulk);
        bulkUpdate(this.midEQParameter, json.midEQ, bulk);
        bulkUpdate(this.highEQParameter, json.highEQ, bulk);
        bulkUpdate(this.feedbackParameter, json.feedback, bulk);
        bulkUpdate(this.wetParameter, json.wet, bulk);
        bulkUpdate(this.rateParameter, json.rate, bulk);
        if (json.drive) {
            bulkUpdate(this.driveParameter, json.drive, bulk);
        }
        
        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'lowEQParameter', this.lowEQParameter, isCopy),
            ... this.loadParameterFromJson(json, 'driveParameter', this.driveParameter, isCopy),
            ... this.loadParameterFromJson(json, 'midEQParameter', this.midEQParameter, isCopy),
            ... this.loadParameterFromJson(json, 'highEQParameter', this.highEQParameter, isCopy),
            ... this.loadParameterFromJson(json, 'feedbackParameter', this.feedbackParameter, isCopy),
            ... this.loadParameterFromJson(json, 'rateParameter', this.rateParameter, isCopy),
            ... this.loadParameterFromJson(json, 'wetParameter', this.wetParameter, isCopy)
        };
    }
}

export default TapeDelay;
